import { T, useTranslate } from '@tolgee/react';
import { Progress } from 'antd';
import { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Countdown from 'react-countdown';
import styled from 'styled-components';

import theme from '../../assets/theme';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { useClickerControls } from '../../hooks/useClickerControls';
import useEndGame from '../../hooks/useEndGame';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import useGame from '../../hooks/useGame';
import { useGameParticipants } from '../../hooks/useGameParticipants';
import useGameSaveSocket from '../../hooks/useGameSaveSocket';
import useGreatPicksStatus from '../../hooks/useGreatPicksStatus';
import useLobby from '../../hooks/useLobby';
import { HostPresentationMediaPrefetcher } from '../../hooks/useMediaPreFetcher';
import { useMetrics } from '../../hooks/useMetrics';
import useResponses from '../../hooks/useResponses';
import useShowMaintanancePopup from '../../hooks/useShowMaintanancePopup';
import { AiFeedbackOverlayVisibleProvider } from '../../providers/AiFeedbackOverlayVisibleProvider';
import { useHideIntercom } from '../../providers/CustomIntercomStateProvider';
import LiveActionsDisabledProvider from '../../providers/LiveActionsDisabledProvider';
import {
  LiveLessonClickerProvider,
  useRegisterClickerHandlers,
  useRegisterClickerLiveState,
} from '../../providers/LiveLessonClickerProvider';
import {
  LivePassageProvider,
  useLivePassageAggregate,
} from '../../providers/LivePassageProvider';
import { TimesyncContext } from '../../providers/TimesyncProvider/TimesyncProvider';
import { Game } from '../../services/backendService/types';
import { hasScorePromptOutput } from '../../utils/aiFeedbackScore';
import {
  hasFeedbackBeenGiven,
  hasModalBeenOpened,
  setFeedbackGiven,
  setModalOpened,
} from '../../utils/liveGameStateStorage';
import {
  getHasNextActivityOrSlide,
  getHasPreviousActivityOrSlide,
  getRoundCategory,
  RoundState,
} from '../../utils/roundUtils';
import { zIndexes } from '../../utils/zIndexes';
import SimpleLiveGameWidgetV2 from '../_molecules/SimpleLiveGameWidget/SimpleLiveGameWidgetV2';
import { PinCodeNudger } from '../SlideComponents/PinCodeNudger';
import { findRelatedSlidesWithoutIndex } from '../SlideComponents/slideUtil';
import { CSSSlideWrapperViewer } from '../SlideComponents/SlideWrapperViewer';
import { CenteredContainer } from '../styled/CenteredContainer';
import AIFeedbackOverlay from './AIFeedbackOverlay';
import EndOfLessonOverlay from './EndOfLessonOverlay/EndOfLessonOverlay';
import GreatPicksGeneratingOverlay from './GreatPicksGeneratingOverlay/GreatPicksGeneratingOverlay';
import SimpleLiveGameActionButtons from './SimpleLiveGameActionButtons';

// The clicker hook + state-registration hooks need to live inside the
// LiveLessonClickerProvider, so the body of the lesson view is split
// into LiveGameContent below. The default export wraps it.
const LiveGameContent = () => {
  useShowMaintanancePopup();
  const { now } = useContext(TimesyncContext);
  const router = useRouter();
  const metrics = useMetrics();
  useHideIntercom();

  const { gameId, tenantId } = router.query as { gameId: string; tenantId: string };
  const { participants, count: participantsCount } = useGameParticipants(
    gameId,
    tenantId,
  );
  const { t } = useTranslate();
  const { autoSave } = useGameSaveSocket();
  const [moderationTooltipHasBeenShown, setModerationTooltipHasBeenShown] = useState(
    false,
  );

  const { game, isFetching, roundState, setRoundState } = useGame(gameId);
  const { end, endResultIsLoading } = useEndGame();

  const isFirstRoundOfActivity = getIsFirstSlideOfActivity(game);
  const [showActivityMedia, setShowActivityMedia] = useState(false);
  const isActivityWithSkip = getIsActivityWithSkip(roundState, game);
  const [isEndGameModalVisible, setIsEndGameModalVisible] = useState(false);
  const [showAIFeedbackOverlay, setShowAIFeedbackOverlay] = useState(false);
  const { setShowLobby } = useLobby(game?.id);
  const canStartActivity = isFirstRoundOfActivity && roundState === 'pre';
  const currentSlide = useMemo(
    () => game?.slides?.find(r => r.id === game?.currentRound),
    [game],
  );

  const responseSlideNotWasPlayed =
    !currentSlide?.wasPlayed &&
    (currentSlide?.type === 'podium' || currentSlide?.type === 'responsegrid');
  const showResultView = useMemo(() => {
    return roundState === 'post' && showActivityMedia === false;
  }, [roundState, showActivityMedia]);

  const isForceOpenGreatPicks = useFeatureFlag('force_open_great_picks');

  // The chain of slides related to the current slide (activity → voting →
  // responsegrid, etc.). All the downstream "is this a foo results slide?"
  // checks need this; compute once per render instead of four times.
  const relatedSlides = useMemo(() => {
    if (!currentSlide || !game?.slides) return [];
    return findRelatedSlidesWithoutIndex(currentSlide.id, game.slides);
  }, [currentSlide, game?.slides]);

  // Check if we're on an AI feedback results slide and if any modal has been opened
  const isAIFeedbackResultsSlide = useMemo(() => {
    if (currentSlide?.type !== 'responsegrid') return false;
    const activitySlide = relatedSlides[0];
    return (
      activitySlide?.type === 'aiAssessedAnswers' ||
      activitySlide?.type === 'aiAssessedImageAnswers'
    );
  }, [currentSlide?.type, relatedSlides]);

  // True when the related activity has a `score` prompt output AND a maxScore
  // set — the host walks scoreboard → feedback overlay → strength view rather
  // than the legacy overlay-first order. The dispatcher reads this to know
  // when to insert the extra "show overlay" press after the scoreboard.
  const isAiFeedbackScoreboardActive = useMemo(() => {
    if (!isAIFeedbackResultsSlide) return false;
    const activitySlide = relatedSlides[0];
    if (!activitySlide) return false;
    if (activitySlide.maxScore == null) return false;
    return hasScorePromptOutput(activitySlide);
  }, [isAIFeedbackResultsSlide, relatedSlides]);
  const isReviewResultsSlide = useMemo(() => {
    return currentSlide?.type === 'responsegrid' || currentSlide?.type === 'podium';
  }, [currentSlide?.type]);

  // Find the slide ID that has the responses (for tracking modal opens)
  const responseSlideIdForTracking = useMemo(() => {
    if (!currentSlide) return undefined;
    if (relatedSlides.length > 0) return relatedSlides[0].id;
    return currentSlide.id;
  }, [currentSlide, relatedSlides]);

  // Whether the current results slide came from a regular answers/canvas activity
  // (so the new "great picks" force-open + overlay flow applies, gated by flag).
  const isAnswersOrCanvasResultsSlide = useMemo(() => {
    if (!isReviewResultsSlide) return false;
    const sourceSlide = relatedSlides[0];
    return sourceSlide?.type === 'answers' || sourceSlide?.type === 'canvas';
  }, [isReviewResultsSlide, relatedSlides]);

  const isForceOpenGreatPicksActive =
    isForceOpenGreatPicks && isAnswersOrCanvasResultsSlide;

  const {
    isGenerating: isGreatPicksGenerating,
    skip: skipGreatPicksOverlay,
  } = useGreatPicksStatus({
    gameId: gameId || '',
    slideId: responseSlideIdForTracking || '',
    tenantId,
    enabled: isForceOpenGreatPicksActive,
  });

  // Check if at least one modal has been opened for this slide
  const [hasModalBeenOpenedState, setHasModalBeenOpenedState] = useState(false);

  // Track whether feedback has been shown/given for slides (local React source of truth)
  const [feedbackGivenBySlideId, setFeedbackGivenBySlideId] = useState<
    Record<string, boolean>
  >({});

  // Get responses count to check if there are any responses
  const { count: responsesCount } = useResponses(
    gameId,
    responseSlideIdForTracking || '',
    true,
    tenantId,
  );

  // Check if we should prevent closing the window
  const isAIActivity = useMemo(() => {
    return (
      currentSlide?.type === 'aiAssessedAnswers' ||
      currentSlide?.type === 'aiAssessedImageAnswers'
    );
  }, [currentSlide?.type]);

  const isTimerRunning = roundState === 'in';

  // Prevent closing the lesson during AI feedback/whiteboard activities while timer is running
  useBeforeUnload({
    enabled: isAIActivity && isTimerRunning,
  });

  const handleUnCompleteGame = useCallback(() => {
    if (!game?.id) return;
    autoSave({
      game: {
        ...game,
        isComplete: false,
      },
    });
  }, [game, autoSave]);

  // If there are no responses, ensure the AI feedback overlay is never shown
  useEffect(() => {
    if (showAIFeedbackOverlay && responsesCount === 0) {
      setShowAIFeedbackOverlay(false);
    }
  }, [showAIFeedbackOverlay, responsesCount]);

  useEffect(() => {
    if (
      (isAIFeedbackResultsSlide || isReviewResultsSlide || isForceOpenGreatPicksActive) &&
      responseSlideIdForTracking &&
      game?.id
    ) {
      const hasOpened = hasModalBeenOpened(game.id, responseSlideIdForTracking);
      setHasModalBeenOpenedState(hasOpened);
      return;
    }

    setHasModalBeenOpenedState(true);
  }, [
    isAIFeedbackResultsSlide,
    responseSlideIdForTracking,
    game?.id,
    isReviewResultsSlide,
    isForceOpenGreatPicksActive,
  ]);

  // Hydrate feedback-given state for the current slide from localStorage (one-time per slide).
  // Also hydrate the related response slide's flag: on a responsegrid slide,
  // `responseSlideIdForTracking` resolves to the ACTIVITY slide id — which is
  // the key the scoreboard-activity branch of handleSeeResults writes to (and
  // the dispatcher reads via `hasFeedbackOverlayBeenShown`). Without this, a
  // page refresh mid-scoreboard would re-show the feedback overlay because
  // the React state default of `undefined` is treated as "not given".
  useEffect(() => {
    if (!game?.id || !currentSlide?.id) return;
    setFeedbackGivenBySlideId(prev => {
      const idsToHydrate = [currentSlide.id];
      if (responseSlideIdForTracking && responseSlideIdForTracking !== currentSlide.id) {
        idsToHydrate.push(responseSlideIdForTracking);
      }
      let next = prev;
      for (const id of idsToHydrate) {
        if (next[id] !== undefined) continue;
        next = { ...next, [id]: hasFeedbackBeenGiven(game.id, id) };
      }
      return next;
    });
  }, [game?.id, currentSlide?.id, responseSlideIdForTracking]);

  const isFeedbackGivenForSlide = useCallback(
    (slideId: string) => {
      return feedbackGivenBySlideId[slideId] === true;
    },
    [feedbackGivenBySlideId],
  );

  const markFeedbackGivenForSlide = useCallback(
    (slideId: string) => {
      if (!game?.id) return;
      setFeedbackGivenBySlideId(prev => ({ ...prev, [slideId]: true }));
      setFeedbackGiven(game.id, slideId);
    },
    [game?.id],
  );

  // Listen for event when modal is opened (update local state + persist)
  useEffect(() => {
    if (
      (isAIFeedbackResultsSlide || isReviewResultsSlide || isForceOpenGreatPicksActive) &&
      responseSlideIdForTracking &&
      game?.id
    ) {
      const handleModalOpened = (event: Event) => {
        const detail = (event as CustomEvent<{ gameId?: string; slideId?: string }>)
          .detail;
        if (!detail?.gameId || !detail?.slideId) return;
        if (detail.gameId !== game.id) return;
        if (detail.slideId !== responseSlideIdForTracking) return;

        setHasModalBeenOpenedState(true);
        setModalOpened(detail.gameId, detail.slideId);
      };

      window.addEventListener('aiModalOpened', handleModalOpened);

      return () => {
        window.removeEventListener('aiModalOpened', handleModalOpened);
      };
    }
  }, [
    isReviewResultsSlide,
    isAIFeedbackResultsSlide,
    isForceOpenGreatPicksActive,
    responseSlideIdForTracking,
    game?.id,
  ]);

  const onCountDownCompleted = useCallback(() => {
    if (game?.roundFreezedAt) return;
    if (game?.slides) {
      if (game && game.slides) {
        const currentSlideIndex = game.currentRound
          ? game.slides.findIndex(round => round.id === game.currentRound)
          : -1;
        const currentSlide = game.slides[currentSlideIndex];
        const newSlides = game.slides.map(r =>
          r.id === currentSlide.id ? { ...currentSlide, wasPlayed: true } : r,
        );
        const nextSlide = game.slides[currentSlideIndex + 1];
        const shouldAutoProgressToNextRound =
          currentSlide.type === 'voting' &&
          getRoundCategory(nextSlide.type) === 'results';

        autoSave({
          game: {
            ...game,
            slides: newSlides,
            currentRound: shouldAutoProgressToNextRound ? nextSlide.id : currentSlide.id,
            modifiedAt: now(),
          },
        });
      }
    }
  }, [game, autoSave, now]);
  const handleTopbarPinClicked = useCallback(() => {
    metrics.logEvent('LiveGame.Pin.Click');
    metrics.logEvent('LiveGame.Lobby.Open', { byPinClick: true });
    setShowLobby(true);
  }, [metrics, setShowLobby]);
  const handleSetSlideWasPlayed = useCallback(
    (slideId: string) => {
      if (game?.slides) {
        const slide = game.slides.find(r => r.id === slideId);

        if (!slide) return;
        if (!slide.wasPlayed) {
          autoSave({
            game: {
              ...game,
              slides: game.slides.map(r =>
                r.id === slideId ? { ...r, wasPlayed: true } : r,
              ),
              modifiedAt: now(),
            },
          });
        }
      }
    },
    [autoSave, game, now],
  );

  const slideWasPlayed = useCallback(() => {
    currentSlide && handleSetSlideWasPlayed(currentSlide.id);
  }, [currentSlide, handleSetSlideWasPlayed]);
  const backgroundMedia = useMemo(() => {
    if (!currentSlide) return undefined;
    if (!game?.slides) return undefined;
    const relatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, game.slides);
    return relatedSlides[0]?.backgroundMedia;
  }, [currentSlide, game?.slides]);

  const handleViewReport = useCallback(() => {
    if (!game) return;
    if (game.isPreviewGame) {
      metrics.logEvent('PreviewLesson.EndPresentation.FromOverlay', {
        gameId: game.id,
        tenantId,
      });
      end({
        gameId: game.id,
      });
      router.back();
      return;
    }
    metrics.logEvent('LiveGame.EndPresentation.FromOverlay', {
      gameId: game.id,
      tenantId,
    });
    end({
      gameId: game.id,
    });
    router.push(`/${tenantId}/reports/${game.id}`);
  }, [game, metrics, tenantId, end, router]);

  const toggleSlideOrResults = useCallback(() => {
    setShowActivityMedia(!showActivityMedia);
  }, [showActivityMedia]);

  // ── Presentation-clicker integration ──────────────────────────────────
  // A clicker is just a USB/BT keyboard that emits PageDown / PageUp / Space
  // / Enter. We register the lesson-level slice of state into the
  // LiveLessonClickerProvider so the central decideNextAction has fresh
  // inputs at keypress time. Other components (Widget, ActionButtons,
  // ResponseGridView) register their own slices.
  const nextSlide = useMemo(() => {
    if (!game?.slides || !currentSlide) return undefined;
    const idx = game.slides.findIndex(r => r.id === currentSlide.id);
    if (idx >= 0 && idx + 1 < game.slides.length) return game.slides[idx + 1];
    return undefined;
  }, [game?.slides, currentSlide]);

  const livePassage = useLivePassageAggregate();
  // Activity slides keep the existing clicker behaviour; the passage
  // shortcut is reserved for plain content slides (type==='slide').
  const isActivitySlide = !!currentSlide && currentSlide.type !== 'slide';

  // Host-side "Show word cloud?" overlay. Owned here so the clicker can
  // reveal the cloud on the first press of a finished wordcloud slide
  // (the on-screen orange Ready button does the same thing). Resets
  // whenever the active slide changes so each wordcloud round starts
  // with the overlay up again.
  const [showWordcloud, setShowWordcloud] = useState(false);
  useEffect(() => {
    setShowWordcloud(false);
  }, [currentSlide?.id]);
  const isWordcloudOverlayVisible =
    currentSlide?.type === 'wordcloud' && !!currentSlide.wasPlayed && !showWordcloud;

  useRegisterClickerLiveState({
    roundState,
    isEndGameModalVisible,
    isLessonComplete: !!game?.isComplete,
    showAIFeedbackOverlay: !!showAIFeedbackOverlay,
    isAIFeedbackResultsSlide,
    canStartActivity,
    isFrozen: !!game?.roundFreezedAt,
    nextSlideType: nextSlide?.type,
    isReviewResultsSlide,
    hasResponses: responsesCount > 0,
    isGreatPicksGenerating: !!isGreatPicksGenerating,
    hasStrengthReviewBeenOpened: hasModalBeenOpenedState,
    isAiFeedbackScoreboardActive,
    // Sourced from the same per-slide "feedback given" storage the widget
    // writes to. Once handleSeeResults shows the overlay (and marks it), this
    // flips to true and the dispatcher's scoreboard branch becomes inert.
    hasFeedbackOverlayBeenShown:
      isFeedbackGivenForSlide(responseSlideIdForTracking ?? '') ||
      !!showAIFeedbackOverlay,
    hasAttachmentElement: livePassage.hasAttachmentElement,
    isPassageOpen: livePassage.isPassageOpen,
    hasPassageBeenOpened: livePassage.hasPassageBeenOpened,
    isActivitySlide,
    isWordcloudOverlayVisible,
  });

  // EndOfLessonOverlay → "View Report". Routed via the central
  // viewReport handler so a clicker press on the last-slide overlay does
  // exactly what the on-screen button does.
  useRegisterClickerHandlers({
    viewReport: handleViewReport,
    openPassage: livePassage.openNext,
    closePassage: livePassage.closeCurrent,
    showWordcloud: () => setShowWordcloud(true),
  });

  useClickerControls({
    // Stay enabled when game.isComplete: the EndOfLessonOverlay is up
    // and the next press should fire viewReport. Stay enabled when the
    // lobby is showing too — decideNextAction's lobby branch routes the
    // forward key to startLesson (or noop with no participants yet).
    enabled: !!game && !!currentSlide,
    onFirstUse: () => {
      metrics.logEvent('LiveGame.Clicker.FirstUse', {
        gameId: game?.id,
        tenantId,
      });
    },
  });

  return (
    <LiveActionsDisabledProvider>
      <AiFeedbackOverlayVisibleProvider
        visible={!!showAIFeedbackOverlay && responsesCount > 0}
      >
        {game?.slides && (
          <HostPresentationMediaPrefetcher tenantId={tenantId} slides={game?.slides} />
        )}
        {game && game.slides && (
          <GameContainer>
            {currentSlide && (
              <>
                <CSSSlideWrapperViewer
                  aspect={{ height: 1080, width: 1920 }}
                  slide={currentSlide}
                  refetchData={true}
                  backgroundMedia={backgroundMedia}
                  resultView={showResultView}
                  showStaticPoll={roundState !== 'post'}
                  hideLogo={false}
                  gameId={gameId}
                  tenantId={tenantId}
                  onSlideWasPlayedTimeout={slideWasPlayed}
                  slides={game.slides}
                  isDiscoverTemplate={false}
                  lessonAttachments={game.attachments}
                  toggleSlideOrResults={toggleSlideOrResults}
                  showWordcloudOverride={showWordcloud}
                  onShowWordcloudChange={setShowWordcloud}
                >
                  <>
                    <PinCodeNudger
                      gameId={gameId}
                      onPinClick={handleTopbarPinClicked}
                      backgroundBrightness={currentSlide.backgroundMedia?.brightness}
                    />

                    {game.roundStartsAt && roundState === 'countdown' && (
                      <ActivityStartingCountdown
                        date={new Date(game.roundStartsAt)}
                        setRoundState={setRoundState}
                      />
                    )}
                  </>
                </CSSSlideWrapperViewer>
                {/* AI Feedback Overlay - renders on top of everything */}
                {/* Only show overlay if there are responses to review */}
                {showAIFeedbackOverlay && responsesCount > 0 && (
                  <AIFeedbackOverlay visible={!!showAIFeedbackOverlay} />
                )}
                {/* Great Picks Generating Overlay - host-only loader for non-AI
                  answers/canvas results while strengths are being generated */}
                {isForceOpenGreatPicksActive && responsesCount > 0 && (
                  <GreatPicksGeneratingOverlay
                    visible={isGreatPicksGenerating}
                    onSkip={skipGreatPicksOverlay}
                  />
                )}
                {/* End of Lesson Overlay */}
                <EndOfLessonOverlay
                  visible={game.isComplete && !isEndGameModalVisible} // visible only when the game is complete and you are not viewing the settings end game modal
                  onViewReport={handleViewReport}
                  onContinueLesson={handleUnCompleteGame}
                  isLoadingReport={endResultIsLoading}
                  isPreview={game.isPreviewGame}
                />
                <SimpleLiveGameActionButtons
                  game={game}
                  slides={game.slides}
                  currentSlide={currentSlide}
                  isNavigationDisabled={Boolean(
                    !isActivityWithSkip ||
                      responseSlideNotWasPlayed ||
                      !!showAIFeedbackOverlay ||
                      (isForceOpenGreatPicksActive && isGreatPicksGenerating) ||
                      ((isAIFeedbackResultsSlide ||
                        isReviewResultsSlide ||
                        isForceOpenGreatPicksActive) &&
                        !showAIFeedbackOverlay &&
                        hasModalBeenOpenedState === false &&
                        responsesCount > 0), // Only disable if there are responses to open
                  )}
                  setShowMedia={setShowActivityMedia}
                  tenantId={tenantId}
                  roundState={roundState}
                  setRoundState={setRoundState}
                  participants={participants}
                  participantsCount={participantsCount}
                  showActivityMedia={showActivityMedia}
                  isEndGameModalVisible={isEndGameModalVisible}
                  setGameModalVisible={setIsEndGameModalVisible}
                />
                {!game.isShowingLobby && !game.isComplete && (
                  <SimpleLiveGameWidgetV2
                    game={game}
                    roundState={roundState}
                    tenantId={tenantId}
                    moderationTooltipHasBeenShown={moderationTooltipHasBeenShown}
                    setModerationTooltipHasBeenShown={setModerationTooltipHasBeenShown}
                    canStartActivity={canStartActivity}
                    setShowMedia={setShowActivityMedia}
                    onCountDownCompleted={onCountDownCompleted}
                    currentSlide={currentSlide}
                    participantCount={participantsCount}
                    showAIFeedbackOverlay={showAIFeedbackOverlay}
                    setShowAIFeedbackOverlay={setShowAIFeedbackOverlay}
                    hasModalBeenOpened={hasModalBeenOpenedState}
                    isFeedbackGivenForSlide={isFeedbackGivenForSlide}
                    markFeedbackGivenForSlide={markFeedbackGivenForSlide}
                  />
                )}
              </>
            )}
          </GameContainer>
        )}
        {!game && !isFetching && (
          <CenteredContainer style={{ paddingTop: 20, height: '100%' }}>
            <h1>
              {t(
                'UpsGameDoesNotExists',
                'Oops this game does not exist, please go back and recreate from the lesson',
              )}
            </h1>
          </CenteredContainer>
        )}
      </AiFeedbackOverlayVisibleProvider>
    </LiveActionsDisabledProvider>
  );
};

export const calculateRemainingTime = (now: () => number, end: number): number => {
  const flooredSeconds = Math.floor((end - now()) / 1000);
  return flooredSeconds;
};

export const toActivity = (type: string) => {
  if (type === 'answers')
    return <T keyName="Written Answers" defaultValue="Written Answers" />;
  if (type === 'aiAssessedAnswers')
    return <T keyName="AI Feedback" defaultValue="AI Feedback" />;
  if (type === 'aiAssessedImageAnswers')
    return <T keyName="AI Whiteboard" defaultValue="AI Whiteboard" />;
  if (type === 'canvas') return <T keyName="Drawings" defaultValue="Drawings" />;
  if (type === 'wordcloud') return <T keyName="Wordcloud" defaultValue="Wordcloud" />;
  if (type === 'ideas') return <T keyName="Ideas" defaultValue="Ideas" />;
  if (type === 'poll') return <T keyName="Poll" defaultValue="Poll" />;
  if (type === 'voting') return <T keyName="Votes" defaultValue="Votes" />;
  if (type === 'text_image') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'image_text') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'image') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'fullscreen_media') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'header') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'bullets') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'slide') return <T keyName="Slide" defaultValue="Slide" />;
};

export const getHasNextActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;
  return getHasNextActivityOrSlide(game.slides, game.currentRound);
};

export const getHasPreviousActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;
  return getHasPreviousActivityOrSlide(game.slides, game.currentRound);
};

const getIsFirstSlideOfActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;

  const relatedSlides = findRelatedSlidesWithoutIndex(game.currentRound, game.slides);
  const firstSlide = relatedSlides[0];
  if (!firstSlide) return false;
  return firstSlide.id === game.currentRound && firstSlide.type !== 'slide';
};

export const getIsLastSlideOfActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;

  const relatedSlides = findRelatedSlidesWithoutIndex(game.currentRound, game.slides);
  const lastSlides = relatedSlides[relatedSlides.length - 1];
  return lastSlides.id === game.currentRound && lastSlides.type !== 'slide';
};

export const getRoundState = ({
  game,
  now,
}: {
  game?: Game;
  now: () => number;
}): RoundState | undefined => {
  //TODO: Add unit tests to this util function now that we have added post-between
  if (!game) return undefined;

  const currentSlide = game.slides?.find(r => r.id === game?.currentRound);

  const isNotTheLastSlide =
    currentSlide?.type !== 'podium' &&
    currentSlide?.type !== 'responsegrid' &&
    currentSlide?.type !== 'poll' &&
    currentSlide?.type !== 'wordcloud' &&
    currentSlide?.type !== 'slide';

  const wasPlayed = currentSlide?.wasPlayed;
  if (wasPlayed) {
    return isNotTheLastSlide ? 'post-between' : 'post';
  }
  // The current round has not yet started
  const isPreRoundState = game && game.roundStartsAt === null;
  if (isPreRoundState) return 'pre';

  // The current round is starting in a few seconds
  const isRoundCountdownState =
    game && game.roundStartsAt !== null && game.roundStartsAt >= now();

  if (isRoundCountdownState) return 'countdown';

  // The current round is done soon
  const isActiveRoundState =
    game &&
    game.roundStartsAt !== null &&
    game.roundStartsAt <= now() &&
    currentSlide &&
    currentSlide.durationSeconds &&
    game.roundStartsAt + currentSlide.durationSeconds * 1000 >= now();

  if (isActiveRoundState) return 'in';

  // The current round was done a few seconds ago
  const isPostRoundState =
    game &&
    game.roundStartsAt !== null &&
    currentSlide &&
    currentSlide.durationSeconds &&
    game.roundStartsAt + currentSlide.durationSeconds * 1000 <= now();

  if (isPostRoundState) return isNotTheLastSlide ? 'post-between' : 'post';
  return isNotTheLastSlide ? 'post-between' : 'post';
};
export const getIsActivityWithSkip = (roundState?: string, game?: Game) => {
  if (!game) return true;
  if (!game.slides) return true;
  if (!roundState) return true;

  if (roundState === 'pre') return true;
  if (roundState === 'post') return true;

  return false;
};

const draw = (start: number, end: number, now: () => number) => {
  const res = ((now() - start) / (end - start)) * 100;
  return res;
};

const ActivityStartingCountdown = ({
  date,
  setRoundState,
}: {
  date: Date;
  setRoundState: Dispatch<SetStateAction<RoundState | undefined>>;
}) => {
  const { now } = useContext(TimesyncContext);
  const [start] = useState(now());
  const isDektop = useBreakPoint(900);
  const { t } = useTranslate();
  const [firstStop] = useState(date.getTime() - 200 - (date.getTime() - start) / 2);
  const [showReadyPercent, setShowReadyPercent] = useState(true);
  const [percent, setPercent] = useState(0);
  const [goPercent, setGoPercent] = useState(0);
  const [showGoPercent, setShowGoPercent] = useState(false);

  useEffect(() => {
    if (percent >= 95) {
      setShowReadyPercent(false);
      setShowGoPercent(true);
    }
  }, [percent]);

  useEffect(() => {
    let ref: NodeJS.Timeout;
    if (showGoPercent) {
      const msTicks = 1000 / 60;
      ref = setInterval(() => {
        setGoPercent(draw(firstStop, date.getTime() - 400, now));
      }, msTicks);
    }

    return () => {
      if (ref) clearInterval(ref);
    };
  }, [date, showGoPercent, firstStop, now]);

  useEffect(() => {
    const msTicks = 1000 / 60;
    const ref = setInterval(() => {
      setPercent(draw(start, firstStop, now));
    }, msTicks);

    return () => {
      clearInterval(ref);
    };
  }, [date, start, firstStop, now]);

  const handleCountDownCompleted = useCallback(() => {
    setRoundState('in');
  }, [setRoundState]);

  return (
    <CenteredContainer
      style={{
        flexDirection: 'column',
        backgroundColor: theme.colors.lessTranslucentWhite,
        backdropFilter: 'blur(7px)',
        flex: '1 1 auto',
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        zIndex: zIndexes('LIVEGAME_COUNTDOWN_OVERLAY'),
      }}
    >
      {showReadyPercent && (
        <ProgressBackground $showShadow $width={isDektop ? 400 : 300}>
          <Progress
            strokeColor={theme.colors.orange}
            trailColor={theme.colors.white}
            width={isDektop ? 400 : 300}
            type="circle"
            percent={percent}
            format={() => (
              <span style={isDektop ? { fontSize: '48px' } : { fontSize: '30px' }}>
                {t('Ready')}
              </span>
            )}
          />
        </ProgressBackground>
      )}
      {showGoPercent && (
        <ProgressBackground $showShadow $width={isDektop ? 400 : 300}>
          <Progress
            strokeColor={theme.colors.orange}
            trailColor={theme.colors.white}
            width={isDektop ? 400 : 300}
            type="circle"
            percent={goPercent}
            format={() => (
              <span style={isDektop ? { fontSize: '48px' } : { fontSize: '30px' }}>
                {t('Set')}{' '}
              </span>
            )}
          />
        </ProgressBackground>
      )}
      <Countdown
        now={now}
        date={date}
        renderer={() => null}
        onComplete={handleCountDownCompleted}
      />
    </CenteredContainer>
  );
};
export const SlideWrapperOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const GameContainer = styled.div`
  height: 100%;
  width: 100%;
  /* Reserve scrollbar gutter so its appearance/disappearance can't shrink the
     slide's available width and trigger a ResizeObserver feedback loop that
     leaves the slide rendered smaller than the viewport. */
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  position: relative;
  /* background-color: ${theme.colors.background}; */
  background-color: black;
  z-index: 0;
`;

const ProgressBackground = styled.div<{ $width: number; $showShadow?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 500px;
  margin-top: 10px;
  height: ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  background-color: ${theme.colors.white};
  box-shadow: ${({ $showShadow }) =>
    $showShadow ? '0px 2px 20px rgba(0, 0, 0, 0.2)' : 'none'};

  .ant-progress-text {
    font-size: 22px;
    font-weight: bold;
    color: ${theme.colors.black} !important;
  }
  .ant-progress-status-success .ant-progress-text {
    color: ${theme.colors.black};
  }

  @media (max-width: 990px) {
    .ant-progress-text {
      font-size: 14px;
    }
  }
  @media (max-width: 500px) {
    .ant-progress-text {
      font-size: 10px;
    }
  }
`;

const RootLiveUpdaterText = styled.p`
  color: ${theme.colors.black};
  font-weight: bold;
  text-align: center;
  margin-bottom: 0;
`;

export const LiveUpdaterCounter = styled(RootLiveUpdaterText)`
  font-size: 65px;
  line-height: 65px;

  @media (max-width: 1050px) {
    font-size: 55px;
    line-height: 55px;
  }
  @media (max-width: 550px) {
    font-size: 30px;
    line-height: 30px;
  }
`;
export const LiveUpdaterType = styled(RootLiveUpdaterText)`
  font-size: 50px;
  line-height: 50px;

  @media (max-width: 1050px) {
    font-size: 35px;
    line-height: 35px;
  }
  @media (max-width: 550px) {
    font-size: 20px;
    line-height: 20px;
  }
`;

const LiveGame = () => (
  <LiveLessonClickerProvider>
    <LivePassageProvider>
      <LiveGameContent />
    </LivePassageProvider>
  </LiveLessonClickerProvider>
);

export default LiveGame;
